/**
 * PageDashboard — reusable full dashboard panel for a single page (or all pages).
 *
 * Props:
 *   pageId    — Facebook Page ID to filter by. Omit (or pass undefined) for all pages.
 *   pageMap   — optional lookup map for page names/avatars (for Customers table)
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Users, MessageSquare, ClipboardList, RefreshCw, Download,
  Search, ChevronLeft, ChevronRight, Facebook, Instagram,
  BarChart2, TrendingUp, Filter, X, Flame, AlertCircle, Phone,
  Calendar, ShoppingCart, Info, ChevronDown, ChevronUp, Star,
  Send, CheckSquare, Square,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import {
  workerApi,
  type DashboardStats,
  type MessageStats,
  type WorkerUser,
  type SlotLead,
  type ScenarioStat,
  type PriorityInboxItem,
} from '../../services/workerApi';

// ── helpers ───────────────────────────────────────────────────────────────────

const fmtDate = (ts: number) =>
  ts ? new Date(ts * 1000).toLocaleDateString('vi-VN') : '—';

const fmtDateTime = (ts: number) =>
  ts ? new Date(ts * 1000).toLocaleString('vi-VN') : '—';

const platformColorCls: Record<string, string> = {
  facebook:  'bg-blue-100 text-blue-700',
  instagram: 'bg-pink-100 text-pink-700',
  zalo:      'bg-cyan-100 text-cyan-700',
};

function platformLabel(type: string) {
  return { facebook: 'Facebook', instagram: 'Instagram', zalo: 'Zalo' }[type] ?? type;
}

// ── MiniBarChart ──────────────────────────────────────────────────────────────

export function MiniBarChart({
  data,
  color,
  label,
}: {
  data: { period: string; total: number }[];
  color: string;
  label: string;
}) {
  const { t } = useTranslation('conversation');
  if (!data.length) return <p className="text-xs text-gray-400 py-4 text-center">{t('noData')}</p>;
  const max = Math.max(...data.map((d) => d.total), 1);
  return (
    <div>
      <p className="text-xs text-gray-500 mb-2 font-medium">{label}</p>
      <div className="flex items-end gap-0.5 h-28 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-200">
        {data.map((d) => (
          <div key={d.period} className="flex flex-col items-center group min-w-[18px]">
            <div
              className={`w-full rounded-t transition-all hover:opacity-80 relative ${color}`}
              ref={(el) => { if (el) el.style.height = `${Math.max(4, (d.total / max) * 96)}px`; }}
            >
              <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[9px] font-semibold text-gray-600 opacity-0 group-hover:opacity-100 whitespace-nowrap">
                {d.total}
              </span>
            </div>
            <span className="text-[8px] text-gray-400 mt-1 rotate-45 origin-top-left truncate max-w-[24px]">
              {d.period.slice(-5)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── KpiCard ───────────────────────────────────────────────────────────────────

export function KpiCard({
  icon, label, value, color,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  color: string;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-center gap-4">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${color}`}>
        {icon}
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-800 leading-tight">{value}</p>
        <p className="text-xs text-gray-500">{label}</p>
      </div>
    </div>
  );
}

// ── Pagination ────────────────────────────────────────────────────────────────

export function Pagination({
  offset, limit, total, onChange,
}: {
  offset: number; limit: number; total: number; onChange: (o: number) => void;
}) {
  const { t } = useTranslation('conversation');
  const page = Math.floor(offset / limit) + 1;
  const pages = Math.max(1, Math.ceil(total / limit));
  return (
    <div className="flex items-center gap-2 text-xs text-gray-500 mt-3 justify-end">
      <span>{offset + 1}–{Math.min(offset + limit, total)} / {total}</span>
      <button
        disabled={offset === 0}
        onClick={() => onChange(Math.max(0, offset - limit))}
        className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
        title={t('prevPage')} aria-label={t('prevPage')}
      >
        <ChevronLeft size={14} />
      </button>
      <span className="font-medium">{page}/{pages}</span>
      <button
        disabled={offset + limit >= total}
        onClick={() => onChange(offset + limit)}
        className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
        title={t('nextPage')} aria-label={t('nextPage')}
      >
        <ChevronRight size={14} />
      </button>
    </div>
  );
}

// ── Priority helpers ──────────────────────────────────────────────────────────

export const PRIORITY_CONFIG: Record<number, { labelKey: string; bg: string; border: string; badge: string; icon: React.ReactNode }> = {
  5: { labelKey: 'hottest',        bg: 'bg-red-50',    border: 'border-red-300',    badge: 'bg-red-500 text-white',    icon: <Flame size={13} className="text-red-500" /> },
  4: { labelKey: 'veryInterested', bg: 'bg-orange-50',  border: 'border-orange-300', badge: 'bg-orange-500 text-white', icon: <Flame size={13} className="text-orange-400" /> },
  3: { labelKey: 'considering',    bg: 'bg-yellow-50',  border: 'border-yellow-300', badge: 'bg-yellow-500 text-white', icon: <Star   size={13} className="text-yellow-500" /> },
  2: { labelKey: 'exploring',      bg: 'bg-blue-50',    border: 'border-blue-200',   badge: 'bg-blue-400 text-white',   icon: <Info   size={13} className="text-blue-400" /> },
  1: { labelKey: 'indifferent',    bg: 'bg-gray-50',    border: 'border-gray-200',   badge: 'bg-gray-400 text-white',   icon: <Info   size={13} className="text-gray-400" /> },
};

export function getPriorityCfg(p: number) {
  return PRIORITY_CONFIG[Math.max(1, Math.min(5, p))] ?? PRIORITY_CONFIG[1];
}

function tsAgo(ts: number, t: (key: string, opts?: Record<string, unknown>) => string) {
  if (!ts) return '';
  const diff = Date.now() / 1000 - ts;
  if (diff < 60)    return t('justNow');
  if (diff < 3600)  return t('minutesAgo', { minutes: Math.floor(diff / 60) });
  if (diff < 86400) return t('hoursAgo', { hours: Math.floor(diff / 3600) });
  return t('daysAgo', { days: Math.floor(diff / 86400) });
}

// ── PriorityCard ──────────────────────────────────────────────────────────────

function PriorityCard({
  item, expanded, onToggle, selected, onToggleSelect,
}: {
  item: PriorityInboxItem;
  expanded: boolean;
  onToggle: () => void;
  selected: boolean;
  onToggleSelect: () => void;
}) {
  const { t } = useTranslation('conversation');
  const cfg = getPriorityCfg(item.priority);
  const avatar = item.customer_avatar ||
    `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(item.user_id)}`;

  return (
    <div className={`rounded-xl border ${selected ? 'border-[#3390ec] ring-2 ring-blue-100' : cfg.border} ${cfg.bg} transition-all`}>
      <div className="w-full flex items-center gap-3 px-4 py-3">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
          className="shrink-0 text-gray-400 hover:text-[#3390ec] transition"
          title={selected ? t('deselect') : t('selectToResend')}
        >
          {selected
            ? <CheckSquare size={18} className="text-[#3390ec]" />
            : <Square size={18} />}
        </button>
        <button type="button" onClick={onToggle} className="flex-1 flex items-center gap-3 text-left min-w-0">
          <span className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${cfg.badge}`}>
            {item.priority}
          </span>
          <img src={avatar} alt="" className="w-9 h-9 rounded-full object-cover shrink-0 border border-white shadow-sm" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-gray-800">{item.customer_name}</span>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${cfg.badge}`}>
                {cfg.icon}&nbsp;{item.interest_level}
              </span>
              {item.unread_count > 0 && (
                <span className="bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5">
                  {item.unread_count} {t('unread')}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-0.5 truncate">
              {item.summary || item.last_message || '…'}
            </p>
          </div>
          <div className="shrink-0 flex flex-col items-end gap-1">
            <span className="text-[10px] text-gray-400">{tsAgo(item.last_message_at, t)}</span>
            {expanded ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
          </div>
        </button>
      </div>

      {expanded && (
        <div className="px-4 pb-4 space-y-2.5 border-t border-white/60">
          <div className="pt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {item.what_they_want && (
              <div className="flex items-start gap-2 bg-white/70 rounded-lg px-3 py-2">
                <ShoppingCart size={13} className="text-emerald-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">{t('wantsToBuy')}</p>
                  <p className="text-xs text-gray-700 mt-0.5">{item.what_they_want}</p>
                </div>
              </div>
            )}
            {item.info_provided && (
              <div className="flex items-start gap-2 bg-white/70 rounded-lg px-3 py-2">
                <Phone size={13} className="text-blue-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">{t('provided')}</p>
                  <p className="text-xs text-gray-700 mt-0.5">{item.info_provided}</p>
                </div>
              </div>
            )}
            {item.next_action && (
              <div className="flex items-start gap-2 bg-white/70 rounded-lg px-3 py-2 sm:col-span-2">
                <AlertCircle size={13} className="text-amber-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">{t('nextAction')}</p>
                  <p className="text-xs text-gray-700 font-medium mt-0.5">{item.next_action}</p>
                </div>
              </div>
            )}
          </div>
          {item.leads.length > 0 && (
            <div className="bg-white/70 rounded-lg px-3 py-2">
              <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wide flex items-center gap-1 mb-2">
                <Calendar size={11} /> {t('scenarioData')}
              </p>
              <div className="space-y-2">
                {item.leads.map((lead) => (
                  <div key={lead.id} className="text-xs">
                    <p className="font-medium text-gray-600 mb-1">{lead.scenario_name}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      {Object.entries(lead.collected).map(([k, v]) => (
                        <span key={k} className="text-gray-500">
                          <span className="text-gray-400">{k}:</span> <span className="font-medium text-gray-700">{String(v)}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <p className="text-[10px] text-gray-400 text-right">
            {t('aiAnalysis', { time: tsAgo(item.interest_updated_at, t) })}
          </p>
        </div>
      )}
    </div>
  );
}

// ── PriorityInboxPanel ────────────────────────────────────────────────────────

function PriorityInboxPanel({
  items, loading, expandedId, onToggleExpand, onRefresh, channelId,
}: {
  items: PriorityInboxItem[];
  loading: boolean;
  expandedId: string | null;
  onToggleExpand: (id: string) => void;
  onRefresh: () => void;
  channelId: string;
}) {
  const [minPriority, setMinPriority] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerText, setComposerText] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<{ sent: number; failed: number } | null>(null);
  const [scanRunning, setScanRunning] = useState(false);
  const [scanResult, setScanResult] = useState<{ candidates: number; sent: number } | null>(null);
  const { t } = useTranslation('conversation');

  const filtered = items.filter((i) => i.priority >= minPriority);
  const counts = [5, 4, 3, 2, 1].map((p) => ({ p, n: items.filter((i) => i.priority === p).length }));

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllVisible = () => setSelected(new Set(filtered.map((i) => i.id)));
  const clearSelection = () => setSelected(new Set());

  const handleBatchSend = async () => {
    if (selected.size === 0 || !channelId) return;
    setSending(true);
    setSendResult(null);
    try {
      const res = await workerApi.triggerFollowupBatch(
        channelId,
        Array.from(selected),
        composerText.trim() || undefined,
      );
      setSendResult({ sent: res.sent.length, failed: res.failed.length });
      if (res.sent.length > 0) {
        clearSelection();
        setComposerOpen(false);
        setComposerText('');
        onRefresh();
      }
    } catch (e) {
      console.error('triggerFollowupBatch failed:', e);
      setSendResult({ sent: 0, failed: selected.size });
    } finally {
      setSending(false);
    }
  };

  const handleScanByConfig = async () => {
    if (!channelId) return;
    setScanRunning(true);
    setScanResult(null);
    try {
      const res = await workerApi.triggerFollowupScan(channelId);
      setScanResult({ candidates: res.stats.candidates, sent: res.stats.sent });
      if (res.stats.sent > 0) onRefresh();
    } catch (e) {
      console.error('triggerFollowupScan failed:', e);
    } finally {
      setScanRunning(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <Flame size={15} className="text-red-500" />
            Priority Inbox
            <span className="text-xs font-normal text-gray-400">{items.length} {t('conversationsWithAI')}</span>
          </h2>
          <div className="flex items-center gap-1">
            <span className="text-xs text-gray-400">{t('show')}</span>
            {[1, 2, 3, 4, 5].map((p) => {
              const cfg = getPriorityCfg(p);
              const cnt = counts.find((c) => c.p === p)?.n ?? 0;
              return (
                <button
                  key={p}
                  onClick={() => setMinPriority(minPriority === p ? 1 : p)}
                  className={`px-2 py-0.5 rounded-full text-[10px] font-bold transition-all border ${
                    minPriority === p ? cfg.badge + ' border-transparent' : 'bg-white border-gray-200 text-gray-500'
                  }`}
                >
                  P{p} ({cnt})
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleScanByConfig}
            disabled={!channelId || scanRunning}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600 disabled:opacity-50"
            title="Trigger scan theo cấu hình target_priorities trong Settings"
          >
            <Send size={12} className={scanRunning ? 'animate-pulse' : ''} />
            {scanRunning ? t('scanning') : t('triggerByConfig')}
          </button>
          <button
            onClick={onRefresh}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600"
          >
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
            {t('reload')}
          </button>
        </div>
      </div>

      {scanResult && (
        <div className="text-xs px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700">
          ✅ {t('triggerConfigResult', { candidates: scanResult.candidates, sent: scanResult.sent })}
        </div>
      )}

      {/* Selection toolbar */}
      {(selected.size > 0 || filtered.length > 0) && (
        <div className="flex items-center justify-between flex-wrap gap-2 px-3 py-2 rounded-lg bg-blue-50 border border-blue-100">
          <div className="flex items-center gap-3 text-xs">
            <span className="font-semibold text-[#3390ec]">
              Đã chọn {selected.size}/{filtered.length}
            </span>
            <button onClick={selectAllVisible} className="text-gray-600 hover:text-[#3390ec]">
              Chọn tất cả đang hiển thị
            </button>
            {selected.size > 0 && (
              <button onClick={clearSelection} className="text-gray-500 hover:text-red-500">
                Bỏ chọn
              </button>
            )}
          </div>
          <button
            onClick={() => setComposerOpen(true)}
            disabled={selected.size === 0 || !channelId}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#3390ec] text-white rounded-lg hover:bg-[#2b7dd6] disabled:opacity-50"
          >
            <Send size={12} />
            Gửi nhắc lại ({selected.size})
          </button>
        </div>
      )}

      {sendResult && (
        <div className={`text-xs px-3 py-2 rounded-lg border ${
          sendResult.failed === 0 && sendResult.sent > 0
            ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
            : 'bg-amber-50 border-amber-200 text-amber-700'
        }`}>
          {sendResult.sent > 0 && <>✅ Đã gửi <b>{sendResult.sent}</b> tin nhắc. </>}
          {sendResult.failed > 0 && <>⚠ Thất bại <b>{sendResult.failed}</b>.</>}
        </div>
      )}

      <div className="grid grid-cols-5 gap-2">
        {counts.map(({ p, n }) => {
          const cfg = getPriorityCfg(p);
          return (
            <div key={p} className={`rounded-xl border ${cfg.border} ${cfg.bg} px-3 py-2 text-center`}>
              <p className="text-xl font-bold text-gray-800">{n}</p>
              <p className="text-[10px] text-gray-500 mt-0.5">{t(cfg.labelKey)}</p>
            </div>
          );
        })}
      </div>

      {loading ? (
        <div className="py-10 text-center text-sm text-gray-400">{t('loading')}</div>
      ) : filtered.length === 0 ? (
        <div className="py-10 text-center text-sm text-gray-400">
          {t('noData')}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((item) => (
            <PriorityCard
              key={item.id}
              item={item}
              expanded={expandedId === item.id}
              onToggle={() => onToggleExpand(item.id)}
              selected={selected.has(item.id)}
              onToggleSelect={() => toggleSelect(item.id)}
            />
          ))}
        </div>
      )}

      {/* Composer modal */}
      {composerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                <Send size={14} className="text-[#3390ec]" />
                Gửi nhắc lại tới {selected.size} khách
              </h3>
              <button onClick={() => setComposerOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X size={16} />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-gray-500">
                Nhập nội dung tin nhắn nhắc. Bỏ trống → dùng <b>mẫu/AI</b> trong cấu hình followup của bot.
              </p>
              <textarea
                rows={4}
                value={composerText}
                onChange={(e) => setComposerText(e.target.value)}
                placeholder="Bạn ơi, mình vẫn ở đây nếu cần hỗ trợ thêm nhé! 😊"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-[#3390ec] focus:outline-none resize-none"
              />
              <p className="text-[11px] text-gray-400">
                Hỗ trợ biến: <code className="bg-gray-100 px-1 rounded">{'{customer_name}'}</code>,{' '}
                <code className="bg-gray-100 px-1 rounded">{'{page_name}'}</code>
              </p>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 bg-gray-50">
              <button
                onClick={() => setComposerOpen(false)}
                disabled={sending}
                className="px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Huỷ
              </button>
              <button
                onClick={handleBatchSend}
                disabled={sending}
                className="flex items-center gap-1.5 px-4 py-1.5 text-xs bg-[#3390ec] text-white rounded-lg hover:bg-[#2b7dd6] disabled:opacity-60"
              >
                <Send size={12} />
                {sending ? 'Đang gửi…' : `Gửi cho ${selected.size} khách`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── main component ────────────────────────────────────────────────────────────

const PAGE_SIZE = 50;
type DashTab = 'overview' | 'priority' | 'customers' | 'leads';
type GroupBy  = 'day' | 'month' | 'year';

export interface PageDashboardProps {
  /** Filter all data to this Facebook page_id. Omit for aggregate (all pages). */
  pageId?: string;
  /** Optional map of page_id → {name, avatar} for the customer table's "Trang" column. */
  pageMap?: Record<string, { name: string; avatar: string }>;
  /** When false, skip all worker API calls (direct mode not ready yet). */
  ready?: boolean;
}

export const PageDashboard: React.FC<PageDashboardProps> = ({ pageId, pageMap = {}, ready = true }) => {
  const { t } = useTranslation('conversation');
  const [tab, setTab]           = useState<DashTab>('overview');
  const [groupBy, setGroupBy]   = useState<GroupBy>('day');

  const [stats, setStats]                 = useState<DashboardStats | null>(null);
  const [msgStats, setMsgStats]           = useState<MessageStats | null>(null);
  const [interestStats, setInterestStats] = useState<{ label: string; count: number }[]>([]);
  const [statsLoading, setStatsLoading]   = useState(false);
  const [statsError, setStatsError]       = useState('');

  const [customers, setCustomers]       = useState<WorkerUser[]>([]);
  const [custTotal, setCustTotal]       = useState(0);
  const [custOffset, setCustOffset]     = useState(0);
  const [custSearch, setCustSearch]     = useState('');
  const [custInput, setCustInput]       = useState('');
  const [custLoading, setCustLoading]   = useState(false);

  const [leads, setLeads]               = useState<SlotLead[]>([]);
  const [leadsTotal, setLeadsTotal]     = useState(0);
  const [leadsOffset, setLeadsOffset]   = useState(0);
  const [leadsScenario, setLeadsScenario] = useState('');
  const [leadsLoading, setLeadsLoading] = useState(false);

  const [priorityItems, setPriorityItems]   = useState<PriorityInboxItem[]>([]);
  const [priorityLoading, setPriorityLoading] = useState(false);
  const [expandedId, setExpandedId]         = useState<string | null>(null);

  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scenarioOptions: ScenarioStat[] = stats?.leads_by_scenario ?? [];

  const pageName   = (pid: string) => pageMap[pid]?.name   || pid;
  const pageAvatar = (pid: string) => pageMap[pid]?.avatar || '';

  // ── loaders ───────────────────────────────────────────────────────────────

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    setStatsError('');
    try {
      const [s, is, ms] = await Promise.all([
        workerApi.getStats({ group_by: groupBy, ...(pageId ? { channel_id: pageId } : {}) }),
        workerApi.getInterestStats(pageId),
        workerApi.getMessageStats(pageId),
      ]);
      setStats(s);
      setInterestStats(is.interest_stats ?? []);
      setMsgStats(ms);
    } catch {
      setStatsError(t('workerConnectionError'));
    } finally {
      setStatsLoading(false);
    }
  }, [groupBy, pageId]);

  const loadCustomers = useCallback(async () => {
    setCustLoading(true);
    try {
      const r = await workerApi.getCustomers({
        limit: PAGE_SIZE,
        offset: custOffset,
        ...(custSearch ? { search: custSearch } : {}),
        ...(pageId ? { channel_id: pageId } : {}),
      });
      setCustomers(r.users);
      setCustTotal(r.total);
    } catch { setCustomers([]); }
    finally { setCustLoading(false); }
  }, [custOffset, custSearch, pageId]);

  const loadLeads = useCallback(async () => {
    setLeadsLoading(true);
    try {
      const r = await workerApi.getLeads({
        limit: PAGE_SIZE,
        offset: leadsOffset,
        ...(pageId ? { channel_id: pageId } : {}),
        ...(leadsScenario ? { scenario_id: leadsScenario } : {}),
      });
      setLeads(r.leads);
      setLeadsTotal(r.count);
    } catch { setLeads([]); }
    finally { setLeadsLoading(false); }
  }, [leadsOffset, leadsScenario, pageId]);

  const loadPriority = useCallback(async () => {
    setPriorityLoading(true);
    try {
      const r = await workerApi.getPriorityInbox({ limit: 200, ...(pageId ? { channel_id: pageId } : {}) });
      setPriorityItems(r.items ?? []);
    } catch { setPriorityItems([]); }
    finally { setPriorityLoading(false); }
  }, [pageId]);

  // ── effects ───────────────────────────────────────────────────────────────

  useEffect(() => { if (ready) loadStats(); }, [ready, loadStats]);
  useEffect(() => { if (ready && tab === 'customers') loadCustomers(); }, [ready, tab, loadCustomers]);
  useEffect(() => { if (ready && tab === 'leads') loadLeads(); }, [ready, tab, loadLeads]);
  useEffect(() => { if (ready && tab === 'priority') loadPriority(); }, [ready, tab, loadPriority]);

  // reset offsets when pageId changes
  useEffect(() => { setCustOffset(0); setLeadsOffset(0); setCustSearch(''); setCustInput(''); setLeadsScenario(''); }, [pageId]);

  // ── search debounce ───────────────────────────────────────────────────────

  const handleSearch = (v: string) => {
    setCustInput(v);
    clearTimeout(searchTimer.current ?? undefined);
    searchTimer.current = setTimeout(() => { setCustSearch(v); setCustOffset(0); }, 400);
  };

  // ── excel exports ─────────────────────────────────────────────────────────

  const exportCustomers = async () => {
    try {
      const r = await workerApi.getCustomers({ limit: 2000, offset: 0, ...(pageId ? { channel_id: pageId } : {}), ...(custSearch ? { search: custSearch } : {}) });
      const rows = r.users.map((c) => ({
        'ID khách': c.platform_user_id, 'Tên': c.name, 'Giới tính': c.gender,
        'Platform': c.platform, 'Trang': pageName(c.channel_id), 'Channel ID': c.channel_id,
        'Ngôn ngữ': c.locale, 'Múi giờ': c.timezone, 'Ngày tham gia': fmtDateTime(c.first_seen_at),
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Khách hàng');
      XLSX.writeFile(wb, `customers_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch {}
  };

  const exportLeads = async () => {
    try {
      const r = await workerApi.getLeads({ limit: 2000, offset: 0, ...(pageId ? { channel_id: pageId } : {}), ...(leadsScenario ? { scenario_id: leadsScenario } : {}) });
      const rows = r.leads.map((l) => {
        const base: Record<string, any> = {
          'ID lead': l.id, 'Tên khách': l.user_name, 'ID khách': l.user_id,
          'Kịch bản': l.scenario_name, 'Platform': l.platform, 'Channel ID': l.channel_id,
          'Ngày thu thập': fmtDateTime(l.completed_at),
        };
        for (const [k, v] of Object.entries(l.collected)) base[`[${k}]`] = v;
        return base;
      });
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Leads');
      XLSX.writeFile(wb, `leads_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch {}
  };

  const refreshAll = () => {
    loadStats();
    if (tab === 'customers') loadCustomers();
    if (tab === 'leads') loadLeads();
    if (tab === 'priority') loadPriority();
  };

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">

      {/* KPI strip */}
      <div className="grid grid-cols-4 gap-4">
        <KpiCard icon={<Users size={20} className="text-blue-600" />}             label={t('kpiCustomers')}      value={statsLoading ? '…' : (stats?.customer_count ?? 0)}     color="bg-blue-50" />
        <KpiCard icon={<ClipboardList size={20} className="text-emerald-600" />}  label={t('kpiLeads')}          value={statsLoading ? '…' : (stats?.lead_count ?? 0)}         color="bg-emerald-50" />
        <KpiCard icon={<MessageSquare size={20} className="text-violet-600" />}   label={t('kpiConversations')}  value={statsLoading ? '…' : (stats?.conversation_count ?? 0)} color="bg-violet-50" />
        <KpiCard icon={<BarChart2 size={20} className="text-amber-600" />}        label={t('kpiTodayMessages')}  value={statsLoading ? '…' : (stats?.message_today_count ?? 0)} color="bg-amber-50" />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {([
          ['overview',  t('tabOverview')],
          ['priority',  '🔥 ' + t('tabPriority')],
          ['customers', t('tabCustomers')],
          ['leads',     t('tabLeads')],
        ] as const).map(([tabKey, label]) => (
          <button
            key={tabKey}
            onClick={() => setTab(tabKey)}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
              tab === tabKey ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {label}
          </button>
        ))}
        {/* Refresh on far right */}
        <div className="ml-auto flex items-center pb-1">
          <button
            onClick={refreshAll}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600"
          >
            <RefreshCw size={12} className={statsLoading ? 'animate-spin' : ''} />
            {t('reload')}
          </button>
        </div>
      </div>

      {/* Error */}
      {statsError && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg flex items-center gap-2">
          <X size={15} className="shrink-0" />{statsError}
        </div>
      )}

      {/* ══ TAB: overview ══════════════════════════════════════════════════ */}
      {tab === 'overview' && (
        <div className="space-y-4">
          {/* GroupBy */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 font-medium">{t('viewBy')}</span>
            {(['day', 'month', 'year'] as const).map((g) => (
              <button
                key={g}
                onClick={() => setGroupBy(g)}
                className={`px-3 py-1 text-xs rounded-full transition-colors ${groupBy === g ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                {g === 'day' ? t('day') : g === 'month' ? t('month') : t('year')}
              </button>
            ))}
          </div>

          {/* Trend charts */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <TrendingUp size={14} className="text-blue-500" />Khách hàng mới
              </h3>
              {statsLoading
                ? <div className="h-28 flex items-center justify-center text-xs text-gray-400">{t('loading')}</div>
                : <MiniBarChart data={stats?.customer_trend ?? []} color="bg-blue-400" label={groupBy === 'day' ? t('last30Days') : groupBy === 'month' ? t('last12Months') : t('last5Years')} />}
            </div>
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <TrendingUp size={14} className="text-emerald-500" />Leads thu thập
              </h3>
              {statsLoading
                ? <div className="h-28 flex items-center justify-center text-xs text-gray-400">{t('loading')}</div>
                : <MiniBarChart data={stats?.lead_trend ?? []} color="bg-emerald-400" label={groupBy === 'day' ? t('last30Days') : groupBy === 'month' ? t('last12Months') : t('last5Years')} />}
            </div>
          </div>

          {/* Message stats chart */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <MessageSquare size={14} className="text-amber-500" />
              Tin nhắn khách gửi
              <span className="text-xs text-gray-400 font-normal">(số tin nhắn inbound)</span>
            </h3>
            {statsLoading ? (
              <div className="h-28 flex items-center justify-center text-xs text-gray-400">{t('loading')}</div>
            ) : (() => {
              const periodData = groupBy === 'day'
                ? Object.entries(msgStats?.daily   ?? {}).sort(([a],[b]) => a.localeCompare(b)).slice(-30).map(([period, total]) => ({ period, total }))
                : groupBy === 'month'
                ? Object.entries(msgStats?.monthly ?? {}).sort(([a],[b]) => a.localeCompare(b)).slice(-12).map(([period, total]) => ({ period, total }))
                : Object.entries(msgStats?.yearly  ?? {}).sort(([a],[b]) => a.localeCompare(b)).slice(-5).map(([period, total]) => ({ period, total }));
              const chartLabel = groupBy === 'day' ? t('last30Days') : groupBy === 'month' ? t('last12Months') : t('last5Years');
              const totalPeriod = periodData.reduce((s, d) => s + d.total, 0);
              return (
                <div>
                  <div className="flex items-center gap-4 mb-3 flex-wrap">
                    <span className="text-2xl font-bold text-gray-800">{totalPeriod.toLocaleString()}</span>
                    <span className="text-xs text-gray-400">{chartLabel}</span>
                    {(() => { const today = new Date().toISOString().slice(0,10); const todayCount = msgStats?.daily?.[today] ?? 0; return todayCount > 0 ? <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">{todayCount} hôm nay</span> : null; })()}
                  </div>
                  <MiniBarChart data={periodData} color="bg-amber-400" label="" />
                </div>
              );
            })()}
          </div>

          {/* Leads by scenario */}
          {(stats?.leads_by_scenario?.length ?? 0) > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <ClipboardList size={14} className="text-emerald-500" />
                Leads theo kịch bản hội thoại
              </h3>
              <div className="space-y-2">
                {stats!.leads_by_scenario.map((sc) => {
                  const pct = stats!.lead_count > 0 ? Math.round((sc.total / stats!.lead_count) * 100) : 0;
                  return (
                    <div key={sc.scenario_id} className="flex items-center gap-3 text-xs">
                      <span className="w-40 truncate text-gray-600 font-medium shrink-0">{sc.scenario_name}</span>
                      <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                        <div
                          className="h-full bg-emerald-400 rounded-full flex items-center justify-end pr-2 text-white text-[10px] font-bold transition-all"
                          ref={(el) => { if (el) el.style.width = `${Math.max(pct, 8)}%`; }}
                        >{sc.total}</div>
                      </div>
                      <span className="text-gray-400 w-10 text-right">{pct}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Interest stats */}
          {interestStats.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <BarChart2 size={14} className="text-amber-500" />
                Mức độ quan tâm khách hàng
                <span className="text-xs text-gray-400 font-normal">(AI phân tích)</span>
              </h3>
              <div className="space-y-2">
                {interestStats.map((s) => {
                  const total = interestStats.reduce((acc, x) => acc + x.count, 0);
                  const pct = total > 0 ? Math.round((s.count / total) * 100) : 0;
                  return (
                    <div key={s.label} className="flex items-center gap-3 text-xs">
                      <span className="w-44 truncate text-gray-600 font-medium shrink-0">{s.label}</span>
                      <div className="flex-1 bg-amber-50 rounded-full h-5 overflow-hidden">
                        <div
                          className="h-full bg-amber-400 rounded-full flex items-center justify-end pr-2 text-white text-[10px] font-bold transition-all"
                          ref={(el) => { if (el) el.style.width = `${Math.max(pct, 8)}%`; }}
                        >{s.count}</div>
                      </div>
                      <span className="text-gray-400 w-10 text-right">{pct}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══ TAB: priority ══════════════════════════════════════════════════ */}
      {tab === 'priority' && (
        <PriorityInboxPanel
          items={priorityItems}
          loading={priorityLoading}
          expandedId={expandedId}
          onToggleExpand={(id) => setExpandedId(expandedId === id ? null : id)}
          onRefresh={loadPriority}
          channelId={pageId}
        />
      )}

      {/* ══ TAB: customers ═════════════════════════════════════════════════ */}
      {tab === 'customers' && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <Users size={15} className="text-blue-500" />
              Danh sách khách hàng
              {custLoading && <span className="text-xs text-gray-400 font-normal">{t('loading')}</span>}
            </h2>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Tìm theo tên, ID..."
                  value={custInput}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-300 w-44"
                />
                {custInput && (
                  <button onClick={() => { setCustInput(''); setCustSearch(''); setCustOffset(0); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" title="Xóa">
                    <X size={11} />
                  </button>
                )}
              </div>
              <button
                onClick={exportCustomers}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 text-emerald-700 font-medium"
              >
                <Download size={13} />Xuất Excel
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-100 text-gray-500 text-left">
                  <th className="pb-2 font-medium pr-3">Khách hàng</th>
                  <th className="pb-2 font-medium pr-3">Platform</th>
                  {!pageId && <th className="pb-2 font-medium pr-3">Trang</th>}
                  <th className="pb-2 font-medium pr-3">Giới tính</th>
                  <th className="pb-2 font-medium pr-3">Ngôn ngữ</th>
                  <th className="pb-2 font-medium">Ngày tham gia</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {customers.length === 0 && !custLoading && (
                  <tr><td colSpan={pageId ? 5 : 6} className="py-8 text-center text-gray-400">
                  {custSearch ? 'Không tìm thấy khách hàng nào.' : t('noCustomers')}
                  </td></tr>
                )}
                {customers.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                    <td className="py-2 pr-3">
                      <div className="flex items-center gap-2">
                        {c.avatar
                          ? <img src={c.avatar} alt="" className="w-7 h-7 rounded-full object-cover border border-gray-200 shrink-0" />
                          : <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-xs shrink-0">{(c.name || c.platform_user_id)[0]?.toUpperCase()}</div>}
                        <div className="min-w-0">
                          <p className="font-medium text-gray-700 truncate max-w-[140px]">{c.name || '(không có tên)'}</p>
                          <p className="text-gray-400 truncate max-w-[140px]">{c.platform_user_id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-2 pr-3">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${platformColorCls[c.platform] ?? 'bg-gray-100 text-gray-600'}`}>
                        {platformLabel(c.platform)}
                      </span>
                    </td>
                    {!pageId && (
                      <td className="py-2 pr-3">
                        <div className="flex items-center gap-1.5 min-w-0">
                          {pageAvatar(c.channel_id)
                            ? <img src={pageAvatar(c.channel_id)} alt="" className="w-5 h-5 rounded-full object-cover shrink-0 border border-gray-200" />
                            : <div className="w-5 h-5 rounded-full bg-blue-100 shrink-0 flex items-center justify-center text-[9px] font-bold text-blue-600">{pageName(c.channel_id)[0]?.toUpperCase()}</div>}
                          <span className="text-gray-700 truncate max-w-[140px]" title={c.channel_id}>{pageName(c.channel_id)}</span>
                        </div>
                      </td>
                    )}
                    <td className="py-2 pr-3 text-gray-500">{c.gender === 'male' ? 'Nam' : c.gender === 'female' ? 'Nữ' : '—'}</td>
                    <td className="py-2 pr-3 text-gray-500">{c.locale || '—'}</td>
                    <td className="py-2 text-gray-400">{fmtDate(c.first_seen_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination offset={custOffset} limit={PAGE_SIZE} total={custTotal} onChange={setCustOffset} />
        </div>
      )}

      {/* ══ TAB: leads ═════════════════════════════════════════════════════ */}
      {tab === 'leads' && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <ClipboardList size={15} className="text-emerald-500" />
              Leads thu thập qua kịch bản hội thoại
              {leadsLoading && <span className="text-xs text-gray-400 font-normal">{t('loading')}</span>}
            </h2>
            <div className="flex items-center gap-2 flex-wrap">
              {scenarioOptions.length > 0 && (
                <div className="flex items-center gap-1.5">
                  <Filter size={13} className="text-gray-400" />
                  <select
                    value={leadsScenario}
                    onChange={(e) => { setLeadsScenario(e.target.value); setLeadsOffset(0); }}
                    className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-emerald-300"
                    title="Lọc theo kịch bản" aria-label="Lọc theo kịch bản"
                  >
                    <option value="">Tất cả kịch bản</option>
                    {scenarioOptions.map((s) => (
                      <option key={s.scenario_id} value={s.scenario_id}>{s.scenario_name} ({s.total})</option>
                    ))}
                  </select>
                </div>
              )}
              <button onClick={exportLeads} className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 text-emerald-700 font-medium">
                <Download size={13} />Xuất Excel
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-100 text-gray-500 text-left">
                  <th className="pb-2 font-medium pr-3">Khách hàng</th>
                  <th className="pb-2 font-medium pr-3">Kịch bản</th>
                  <th className="pb-2 font-medium pr-3">Platform</th>
                  <th className="pb-2 font-medium pr-3">Thông tin thu thập</th>
                  <th className="pb-2 font-medium">Ngày</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {leads.length === 0 && !leadsLoading && (
                  <tr><td colSpan={5} className="py-8 text-center text-gray-400">
                    {leadsScenario ? 'Không có lead cho kịch bản này.' : 'Chưa có lead nào được thu thập.'}
                  </td></tr>
                )}
                {leads.map((l) => (
                  <tr key={l.id} className="hover:bg-gray-50 transition-colors">
                    <td className="py-2 pr-3">
                      <div className="flex items-center gap-2">
                        {l.user_avatar
                          ? <img src={l.user_avatar} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
                          : <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 text-[10px] text-gray-500 font-semibold">{(l.user_name || '?')[0].toUpperCase()}</div>}
                        <div className="min-w-0">
                          <p className="font-medium text-gray-700 truncate max-w-[130px]">{l.user_name || '(ẩn danh)'}</p>
                          <p className="text-gray-400 truncate max-w-[130px] font-mono text-[10px]">{l.user_id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-2 pr-3">
                      <span className="bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap">
                        {l.scenario_name}
                      </span>
                    </td>
                    <td className="py-2 pr-3">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${platformColorCls[l.platform] ?? 'bg-gray-100 text-gray-600'}`}>
                        {platformLabel(l.platform)}
                      </span>
                    </td>
                    <td className="py-2 pr-3">
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {Object.entries(l.collected).map(([k, v]) => (
                          <span key={k} className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-[10px]">
                            <span className="font-medium">{k}:</span> {String(v)}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="py-2 text-gray-400 whitespace-nowrap">{fmtDate(l.completed_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination offset={leadsOffset} limit={PAGE_SIZE} total={leadsTotal} onChange={setLeadsOffset} />
        </div>
      )}
    </div>
  );
};
